import Link from "next/link";
import { useTranslation } from "react-i18next";

import LegalPageLayout from "@/my_components/Common/LegalPageLayout";
import CallToActionOne from "@/my_components/CallToAction/CallToActionOne";
import Newsletter from "@/my_components/Newsletters/Newsletter";
import { ROUTE } from "@/route/app_routes.js";
import { CONFIG } from "@/app_config.js";
import { image_url } from "@/helper/Utilities";

const LAST_UPDATED = "September 2026";

const useSections = (t) => {
  const appName = CONFIG.APP_NAME;
  const nickName = CONFIG.NICK_NAME;
  const appNameUpper = CONFIG.APP_NAME.toUpperCase();

  return [
    {
      id: "acceptance",
      navLabel: t("terms.sections.acceptance.navLabel", "Acceptance of Terms"),
      title: t("terms.sections.acceptance.title", "1. Acceptance of Terms"),
      body: (
        <>
          <p>
            {t(
              "terms.sections.acceptance.para1",
              "These Terms of Use (“Terms”) form a binding agreement between you and {{appName}} (“{{nickName}},” “we,” “us,” or “our”), a service operated by SmartTech Solutions. They govern your access to and use of the {{appName}} website, courses, certificates, community features, and events (collectively, the “Service”).",
              { appName, nickName }
            )}
          </p>
          <p>
            {t("terms.sections.acceptance.para2Before", "By creating an account, enrolling in a course, registering for an event, or otherwise using the Service, you confirm that you have read, understood, and agree to be bound by these Terms and by our ")}
            <Link href={ROUTE.privacy}>{t("terms.sections.acceptance.para2LinkText", "Privacy Policy")}</Link>
            {t("terms.sections.acceptance.para2After", ", which is incorporated into these Terms by reference. If you do not agree to these Terms, you must not access or use the Service.")}
          </p>
        </>
      ),
    },
    {
      id: "eligibility",
      navLabel: t("terms.sections.eligibility.navLabel", "Eligibility & Age"),
      title: t("terms.sections.eligibility.title", "2. Eligibility and Age Requirements"),
      body: (
        <>
          <p>
            {t(
              "terms.sections.eligibility.para1",
              "You must be at least 16 years old to create an account and use the Service. By registering, you represent and warrant that you meet this age requirement and that you have the legal capacity to enter into these Terms in your jurisdiction. If you are between 16 and the age of majority where you live, you confirm you have your parent's or legal guardian's permission to use the Service."
            )}
          </p>
          <p>
            {t("terms.sections.eligibility.para2Before", "The Service is not directed at, and we do not knowingly permit registration by, children under 16. See our ")}
            <Link href={ROUTE.privacy}>{t("terms.sections.eligibility.para2LinkText", "Privacy Policy")}</Link>
            {t("terms.sections.eligibility.para2After", " for how we handle this.")}
          </p>
        </>
      ),
    },
    {
      id: "accounts",
      navLabel: t("terms.sections.accounts.navLabel", "Accounts & Security"),
      title: t("terms.sections.accounts.title", "3. Account Registration and Security"),
      body: (
        <>
          <ul>
            <li>{t("terms.sections.accounts.listItem1", "You must provide accurate, current, and complete information when you register, and keep it up to date.")}</li>
            <li>{t("terms.sections.accounts.listItem2", "You are solely responsible for maintaining the confidentiality of your password and for all activity that occurs under your account, whether or not you authorized it.")}</li>
            <li>{t("terms.sections.accounts.listItem3", "You must notify us immediately at the email address in Section 19 if you suspect unauthorized access to, or use of, your account.")}</li>
            <li>{t("terms.sections.accounts.listItem4", "You may enable optional two-factor authentication from your dashboard settings, which requires a one-time 6-digit code sent to your email address each time you log in, in addition to your password. If you enable it, keep your email account secure, since it becomes part of your login flow.")}</li>
            <li>{t("terms.sections.accounts.listItem5", "Accounts are personal: one account per person. Creating multiple accounts to evade platform limits (such as concurrent-device or enrollment limits) or to circumvent a suspension is a violation of these Terms.")}</li>
            <li>{t("terms.sections.accounts.listItem6", "You may not transfer, sell, or share your account credentials with anyone else.")}</li>
          </ul>
          <div className="rbt-legal-callout">
            <i className="feather-alert-circle" aria-hidden="true"></i>
            <p>
              {t("terms.sections.accounts.calloutText", "We will never ask you for your password by email, chat, or phone. Treat any such request as a phishing attempt and report it to us.")}
            </p>
          </div>
        </>
      ),
    },
    {
      id: "the-service",
      navLabel: t("terms.sections.theService.navLabel", "Description of the Service"),
      title: t("terms.sections.theService.title", "4. Description of the Service"),
      body: (
        <>
          <p>
            {t(
              "terms.sections.theService.para1",
              "{{appName}} provides structured, self-paced online courses across subjects including web development, mobile development, cloud computing, blockchain, data, and related technology disciplines. Course access, lesson content, quizzes, and certificates of completion are provided free of charge, with no tuition, subscription fee, or hidden charge for course content.",
              { appName }
            )}
          </p>
          <p>
            {t(
              "terms.sections.theService.para1b",
              "Video lessons are delivered through YouTube's standard embedded video player. We curate, sequence, and organize publicly available third-party YouTube videos into structured, goal-oriented curricula for each course; we do not re-host, download, or store this video content on our own servers. Section 11 below explains what this means for you in detail, including whose content it is and what happens if a video becomes unavailable."
            )}
          </p>
          <p>
            {t("terms.sections.theService.para2", "We also host a calendar of live events, workshops, and webinars. Some events are free to registered users; others require the purchase of a paid ticket, as clearly indicated on the relevant event page before you register. Section 6 below explains how payments, cancellations, and refunds work for paid events.")}
          </p>
          <p>
            {t("terms.sections.theService.para3", "We may add, modify, or discontinue any feature, course, or event at any time. We will act reasonably when doing so, but we do not guarantee that any specific course, feature, or event will remain available indefinitely.")}
          </p>
          <p>
            {t("terms.sections.theService.para4", "The Service is available in multiple languages. Machine- and human-reviewed translations of our interface are provided as a convenience; the English version of the Service and of these Terms is the authoritative version, and in the event of any conflict or ambiguity between a translation and the English original, the English original governs.")}
          </p>
        </>
      ),
    },
    {
      id: "certificates",
      navLabel: t("terms.sections.certificates.navLabel", "Course Access & Certificates"),
      title: t("terms.sections.certificates.title", "5. Course Access and Certificates of Completion"),
      body: (
        <>
          <p>
            {t(
              "terms.sections.certificates.para1",
              "Enrolling in a course grants you access to that course's lessons, exercises, and quizzes for as long as your account remains active and in good standing and the course remains offered on the Service. Completing a course's requirements and passing its graded quiz unlocks a certificate of completion tied to your profile, which you may download as a PDF or share to third-party platforms such as LinkedIn."
            )}
          </p>
          <div className="rbt-legal-callout">
            <i className="feather-award" aria-hidden="true"></i>
            <p>
              {t("terms.sections.certificates.calloutBefore", "A {{appName}} certificate confirms that you completed our curriculum and met its passing requirements. It is ", { appName })}
              <strong>{t("terms.sections.certificates.calloutBold", "not")}</strong>
              {t("terms.sections.certificates.calloutAfter", " an accredited academic degree, diploma, or professional certification issued or endorsed by any government body, accrediting institution, or third-party certification authority, and we make no representation or guarantee as to how any employer, institution, or other third party will regard it.")}
            </p>
          </div>
          <p>
            {t("terms.sections.certificates.para2", "Each certificate carries a unique verification number that can be checked on our public certificate-verification page, allowing third parties to confirm its authenticity and the details of issuance.")}
          </p>
          <p>
            {t(
              "terms.sections.certificates.para3",
              "Some quizzes are time-limited, with the time allowed shown before you begin. If you do not submit before time runs out, the quiz is automatically submitted with whatever answers you had entered at that point, and graded accordingly. We independently verify submission timing on our servers as a safeguard against manipulation of your device's clock or network conditions."
            )}
          </p>
        </>
      ),
    },
    {
      id: "payments",
      navLabel: t("terms.sections.payments.navLabel", "Payments, Tickets & Refunds"),
      title: t("terms.sections.payments.title", "6. Payments, Tickets, and Refunds"),
      body: (
        <>
          <p>
            {t("terms.sections.payments.para1", "Course content is always free. The only paid items on the Service are tickets for select events and workshops. Where an event requires a paid ticket, the price, currency, and any applicable taxes or fees are displayed before you complete checkout, and payment is processed through a third-party payment processor. We do not store your full card details on our own servers.")}
          </p>
          <p>
            {t("terms.sections.payments.freeEventsPara", "Free events do not require payment or an account, but each reservation must use a real, working email and phone number: we allow only one active reservation per email address and per phone number for a given event, and where the email provided is not already a verified account email we require a one-time verification code sent to it before the reservation is confirmed. This is to keep a limited number of free spots available fairly, and reservations found to use fabricated or duplicate contact details may be cancelled without notice.")}
          </p>
          <h3>{t("terms.sections.payments.subheading1", "Cancellations by you")}</h3>
          <ul>
            <li>{t("terms.sections.payments.cancelListItem1", "Tickets cancelled at least 7 days before the event date are eligible for a full refund.")}</li>
            <li>{t("terms.sections.payments.cancelListItem2", "Tickets cancelled between 7 and 2 days before the event date are eligible for a 50% refund.")}</li>
            <li>{t("terms.sections.payments.cancelListItem3", "Tickets cancelled less than 48 hours before the event, or unused (“no-shows”), are non-refundable.")}</li>
          </ul>
          <h3>{t("terms.sections.payments.subheading2", "Cancellations or changes by us")}</h3>
          <p>
            {t("terms.sections.payments.para2", "If we cancel, materially reschedule, or materially change an event you hold a paid ticket for, we will notify you and offer, at your election, a full refund or a transfer of your ticket to the rescheduled date or a comparable event.")}
          </p>
          <p>
            {t("terms.sections.payments.para3", "Refunds are issued to the original payment method and may take several business days to appear, depending on your bank or card issuer. To request a refund or dispute a charge, contact us using the details in Section 19 before initiating a chargeback with your bank, so we have a fair opportunity to resolve the issue directly.")}
          </p>
        </>
      ),
    },
    {
      id: "intellectual-property",
      navLabel: t("terms.sections.intellectualProperty.navLabel", "Intellectual Property"),
      title: t("terms.sections.intellectualProperty.title", "7. Intellectual Property"),
      body: (
        <>
          <p>
            {t(
              "terms.sections.intellectualProperty.para1",
              "Course materials that we author or license ourselves — including our curriculum structure and lesson sequencing, written lesson summaries and notes, quizzes, exercises, graphics, certificate designs, software, and the Service itself (the “Original Course Content”) — are the property of {{appName}}, SmartTech Solutions, or our licensors, and are protected by copyright, trademark, and other intellectual property laws.",
              { appName }
            )}
          </p>
          <p>
            {t(
              "terms.sections.intellectualProperty.para1b",
              "Video lessons are a distinct category, addressed fully in Section 11: they are third-party YouTube videos, owned by their respective creators and/or YouTube, LLC / Google LLC, that we display through YouTube's embedded player as part of our curriculum. We do not own this video content and do not claim any copyright in it — our own intellectual property in a course lies in how we have selected, sequenced, and built a structured learning path around it, together with the Original Course Content we pair with it, not in the underlying footage itself."
            )}
          </p>
          <p>
            {t("terms.sections.intellectualProperty.para2", "Subject to your compliance with these Terms, we grant you a limited, personal, non-exclusive, non-transferable, revocable license to access and use the Original Course Content solely for your own individual, non-commercial learning. This license does not permit you to:")}
          </p>
          <ul>
            <li>{t("terms.sections.intellectualProperty.listItem1", "copy, download (except through features we provide for that purpose), reproduce, or republish Original Course Content outside the Service;")}</li>
            <li>{t("terms.sections.intellectualProperty.listItem2", "modify, create derivative works from, reverse-engineer, or decompile any part of the Service or Original Course Content;")}</li>
            <li>{t("terms.sections.intellectualProperty.listItem3", "sell, rent, lease, sublicense, distribute, or otherwise make Original Course Content available to any third party; or")}</li>
            <li>{t("terms.sections.intellectualProperty.listItem4", "remove any copyright, trademark, or other proprietary notice from Original Course Content.")}</li>
          </ul>
          <p>
            {t("terms.sections.intellectualProperty.para3", "{{appName}}, our logo, and other marks used on the Service are trademarks of SmartTech Solutions or its licensors and may not be used without our prior written permission.", { appName })}
          </p>
          <p>
            {t(
              "terms.sections.intellectualProperty.para4",
              "Optional decorative banner images offered in your dashboard settings are either original {{appName}} designs or properly licensed third-party photography made available for this kind of use; where a photograph requires attribution, we display it in the selection interface. You may not extract, reuse, or redistribute these images outside the Service.",
              { appName }
            )}
          </p>
        </>
      ),
    },
    {
      id: "user-content",
      navLabel: t("terms.sections.userContent.navLabel", "User-Generated Content"),
      title: t("terms.sections.userContent.title", "8. User-Generated Content"),
      body: (
        <>
          <p>
            {t(
              "terms.sections.userContent.para1",
              "“User Content” means anything you submit to the Service, including profile information, comments, forum or discussion posts, quiz or assignment submissions, reviews, and any other material you post or upload. You retain ownership of your User Content."
            )}
          </p>
          <p>
            {t(
              "terms.sections.userContent.para2",
              "By submitting User Content, you grant {{appName}} a worldwide, non-exclusive, royalty-free, sublicensable license to host, store, reproduce, display, adapt (e.g., for formatting), and distribute that User Content solely for the purposes of operating, providing, promoting, and improving the Service. This license ends when you delete the applicable content or your account, except to the extent it has been shared with others who have not deleted it, or we are required to retain it for legal or security reasons.",
              { appName }
            )}
          </p>
          <p>
            {t("terms.sections.userContent.para3", "You are solely responsible for your User Content and represent that you have all rights necessary to submit it and that it does not violate these Terms, any applicable law, or the rights of any third party.")}
          </p>
          <h3>{t("terms.sections.userContent.subheading1", "Course reviews and ratings")}</h3>
          <p>
            {t(
              "terms.sections.userContent.para4",
              "Reviews and star ratings you post about a course are public and attributed to your name. We may remove a review, in whole or in part, if it violates these Terms (for example, Section 9's prohibitions on harassment, abuse, or unlawful content) or a good-faith report from another user. Removing a review does not otherwise affect your account standing."
            )}
          </p>
          <h3>{t("terms.sections.userContent.subheading2", "Personal notes")}</h3>
          <p>
            {t(
              "terms.sections.userContent.para5Before",
              "Notes you take against a lesson are private by default: they are visible only to you, and no other student can see them. They are not treated as User Content we host, display, or distribute under the license above. We do not routinely review note content. If a note is reported or otherwise flagged for a specific, documented reason (for example, a policy or legal concern raised by you or a third party), an authorized administrator may view that note's content to investigate the report; this access is logged. See "
            )}
            <Link href={ROUTE.privacy}>{t("terms.sections.userContent.para5LinkText", "our Privacy Policy")}</Link>
            {t("terms.sections.userContent.para5After", " for more detail on how this works.")}
          </p>
        </>
      ),
    },
    {
      id: "acceptable-use",
      navLabel: t("terms.sections.acceptableUse.navLabel", "Acceptable Use"),
      title: t("terms.sections.acceptableUse.title", "9. Acceptable Use and Prohibited Conduct"),
      body: (
        <>
          <p>{t("terms.sections.acceptableUse.intro", "When using the Service, you agree that you will not:")}</p>
          <ul>
            <li>{t("terms.sections.acceptableUse.listItem1", "cheat on quizzes or assignments, including by using unauthorized aids, sharing answers, or misrepresenting completion of course requirements in a way that undermines the integrity of certificates;")}</li>
            <li>{t("terms.sections.acceptableUse.listItem2", "harass, threaten, defame, or abuse other users, instructors, or staff, or post content that is unlawful, hateful, sexually explicit, or otherwise objectionable;")}</li>
            <li>{t("terms.sections.acceptableUse.listItem3", "impersonate any person or entity, or misrepresent your affiliation with any person or entity;")}</li>
            <li>{t("terms.sections.acceptableUse.listItem4", "use bots, scrapers, crawlers, or other automated means to access, copy, or extract data from the Service beyond what is permitted by our robots.txt and applicable law;")}</li>
            <li>{t("terms.sections.acceptableUse.listItem5", "probe, scan, or test the vulnerability of the Service, or attempt to gain unauthorized access to any account, system, or network connected to the Service;")}</li>
            <li>{t("terms.sections.acceptableUse.listItem6", "resell, sublicense, or otherwise commercially exploit access to courses, certificates, or events; or")}</li>
            <li>{t("terms.sections.acceptableUse.listItem7", "upload malicious code or otherwise interfere with or disrupt the integrity or performance of the Service.")}</li>
          </ul>
          <p>
            {t("terms.sections.acceptableUse.para1", "Reports of suspected violations may be submitted through our support and reporting tools. We review reports and may take any of the actions described in Section 10.")}
          </p>
        </>
      ),
    },
    {
      id: "suspension",
      navLabel: t("terms.sections.suspension.navLabel", "Suspension & Termination"),
      title: t("terms.sections.suspension.title", "10. Account Suspension and Termination"),
      body: (
        <>
          <p>
            {t("terms.sections.suspension.para1Before", "You may stop using the Service and close your account at any time. Your dashboard settings include a self-service account deletion option that, after you confirm your password and a typed confirmation, deactivates your account and signs you out immediately on every device — see ")}
            <Link href={ROUTE.privacy}>{t("terms.sections.suspension.para1LinkText", "Section 7 of our Privacy Policy")}</Link>
            {t("terms.sections.suspension.para1After", " for what happens to your data afterward. You can also request account closure by contacting us.")}
          </p>
          <p>
            {t(
              "terms.sections.suspension.para2",
              "We may suspend or terminate your access to the Service, in whole or in part, if we reasonably determine that you have violated these Terms, engaged in conduct that harms other users, instructors, or the Service, violated applicable law, or if required to do so by law or a competent authority. Determinations may be based on our own review or on reports received through our reporting and moderation tools. Where reasonably possible and not prohibited by law or a legitimate security concern, we will provide notice of the action taken and, where applicable, a reasonable opportunity to respond."
            )}
          </p>
          <p>
            {t("terms.sections.suspension.para3", "Certificates already issued at the time of suspension or termination remain subject to verification, but we reserve the right to revoke a certificate obtained through fraud, cheating, or a material violation of these Terms.")}
          </p>
        </>
      ),
    },
    {
      id: "third-party",
      navLabel: t("terms.sections.thirdParty.navLabel", "YouTube Content & Third Parties"),
      title: t("terms.sections.thirdParty.title", "11. YouTube Video Content, Third-Party Links, and Services"),
      body: (
        <>
          <p>
            {t(
              "terms.sections.thirdParty.para1",
              "The Service may contain links to, or integrate with, third-party websites, resources, or services (for example, a payment processor at checkout, or a third-party site linked from course content). We do not control and are not responsible for the content, policies, or practices of any third party. Your use of third-party sites and services is at your own risk and subject to their own terms and privacy policies."
            )}
          </p>

          <h3>{t("terms.sections.thirdParty.subheading1", "YouTube video content and our reliance on it")}</h3>
          <p>
            {t(
              "terms.sections.thirdParty.para2",
              "{{appName}} is, by design, dependent on YouTube for the video component of its courses. Rather than filming, hosting, or storing our own instructional videos, we research, evaluate, and aggregate existing, publicly embeddable YouTube videos from third-party creators and channels, and organize them — together with our own quizzes, written summaries, and other Original Course Content described in Section 7 — into a structured, world-standard curriculum sequence designed to take a learner from foundational concepts through to practical, project-based competence. This curation, sequencing, and structuring work is ours; the underlying video footage is not.",
              { appName }
            )}
          </p>
          <div className="rbt-legal-callout">
            <i className="feather-youtube" aria-hidden="true"></i>
            <p>
              {t(
                "terms.sections.thirdParty.callout1",
                "We do not own, host, control, or claim any copyright or other intellectual-property interest in the YouTube videos embedded in our courses. That content belongs to its respective creators, channels, and/or YouTube, LLC and Google LLC. Videos are displayed exclusively through YouTube's own embedded player, using the embedding functionality YouTube makes available to publishers for public videos — we do not download, re-host, or redistribute the underlying video files ourselves."
              )}
            </p>
          </div>
          <p>
            {t(
              "terms.sections.thirdParty.para3",
              "“YouTube,” the YouTube logo, and related marks are trademarks of Google LLC. Our use of embedded YouTube videos, and any reference to YouTube on the Service, does not imply, and should not be understood as, any affiliation with, sponsorship by, certification from, or endorsement by YouTube or Google of {{appName}} or our curriculum.",
              { appName }
            )}
          </p>

          <h3>{t("terms.sections.thirdParty.subheading2", "Availability is not guaranteed")}</h3>
          <p>
            {t(
              "terms.sections.thirdParty.para4",
              "Because embedded videos remain under the sole control of their original creators and of YouTube, a video that is part of a course today may become unavailable at any time — for example, if the creator removes it, sets it to private, or takes down their channel, or if YouTube removes it under its own policies, a copyright claim, or a legal requirement — entirely outside our knowledge or control. When we become aware that an embedded video is no longer available, we will make reasonable efforts to replace it with a comparable video or alternative Original Course Content within a reasonable time, but we do not guarantee that a replacement will be available immediately, that it will be identical in content or quality to the original, or that a lesson will remain uninterrupted while we do so."
            )}
          </p>

          <h3>{t("terms.sections.thirdParty.subheading3", "No endorsement or verification of third-party video content")}</h3>
          <p>
            {t(
              "terms.sections.thirdParty.para5",
              "We select videos we believe are accurate, relevant, and well-suited to their place in the curriculum, but we are not the creator, publisher, or fact-checker of this third-party content. We do not independently verify, and do not warrant, the accuracy, completeness, currency, or opinions expressed in any embedded video, and we do not endorse the creator's channel, other videos, or any other material outside the specific video embedded in a lesson. If you believe an embedded video is inaccurate, outdated, or otherwise unsuitable for the curriculum, please tell us using the contact details in Section 19 so we can review it."
            )}
          </p>

          <h3>{t("terms.sections.thirdParty.subheading4", "YouTube's own terms and data practices apply")}</h3>
          <p>
            {t(
              "terms.sections.thirdParty.para6Before",
              "When you play an embedded YouTube video on the Service, you are interacting directly with YouTube/Google's player and infrastructure, in addition to ours. That interaction is governed by "
            )}
            <a href="https://www.youtube.com/t/terms" target="_blank" rel="noopener noreferrer">
              {t("terms.sections.thirdParty.para6LinkText1", "YouTube's own Terms of Service")}
            </a>
            {t("terms.sections.thirdParty.para6Middle", " and ")}
            <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
              {t("terms.sections.thirdParty.para6LinkText2", "Google's Privacy Policy")}
            </a>
            {t(
              "terms.sections.thirdParty.para6After",
              ", not by these Terms or our Privacy Policy. YouTube/Google may independently collect technical and usage data and set its own cookies or similar technologies in connection with embedded video playback — for example, to play the video, measure viewership, or serve its own recommendations — separately from, and in addition to, the data we collect ourselves as described in our Privacy Policy. We do not control, and are not responsible for, YouTube or Google's own data practices."
            )}
          </p>
        </>
      ),
    },
    {
      id: "tutor-content",
      navLabel: t("terms.sections.tutorContent.navLabel", "Tutors & Content Contributors"),
      title: t("terms.sections.tutorContent.title", "12. Tutors and Content Contributors"),
      body: (
        <>
          <p>
            {t(
              "terms.sections.tutorContent.para1",
              "This section applies if you apply to teach on {{appName}} through our \"Apply to teach\" form and we accept and publish a course or other content you submitted (a \"Contributed Course\"). It supplements, and should be read together with, Sections 7 (Intellectual Property) and 8 (User-Generated Content) above.",
              { appName }
            )}
          </p>
          <p>
            {t(
              "terms.sections.tutorContent.para2",
              "{{appName}} reviews every submitted course for teaching quality and value before it goes live, and we may decline a submission, request changes before publishing it, or choose not to publish it at all, at our sole discretion. A decision not to publish a submission is not a statement about you personally or your ability as a tutor — it simply means that particular submission, as submitted, did not meet the bar we hold the catalog to.",
              { appName }
            )}
          </p>
          <div className="rbt-legal-callout">
            <i className="feather-alert-triangle" aria-hidden="true"></i>
            <p>
              <strong>{t("terms.sections.tutorContent.calloutLabel", "Once published, you relinquish rights to that content on the platform:")}</strong>{" "}
              {t(
                "terms.sections.tutorContent.calloutText",
                "once a Contributed Course or any content you submitted for it is published (made live) on {{appName}}, you relinquish your rights to that specific course and its content as published on the Service. {{appName}} retains sole discretion over whether a published Contributed Course remains live, is modified, or is taken down, and when. This is distinct from, and narrower than, the general User Content license in Section 8: it applies specifically to a Contributed Course once it has actually been published, not to a submission that is still under review or that we decline to publish.",
                { appName }
              )}
            </p>
          </div>
          <p>
            {t(
              "terms.sections.tutorContent.para3",
              "In exchange for a published Contributed Course, {{appName}} credits you as its creator on the course itself and gives you growing exposure to our learner base as it expands — we do not pay a fee for Contributed Courses unless we separately agree to do so with you in writing. We also provide a single, clearly delimited space within a Contributed Course for you to reference your own other work (for example, your channel, site, or other courses); this space is deliberately limited and controlled by {{appName}}, and is not a general advertising slot or an invitation to promote unrelated third-party products or services.",
              { appName }
            )}
          </p>
          <p>
            {t(
              "terms.sections.tutorContent.para4",
              "You remain responsible for ensuring that anything you submit through the \"Apply to teach\" form, and any content you provide if we move forward with a Contributed Course, is your own work (or that you hold all rights needed to submit it), does not infringe any third party's rights, and otherwise complies with Section 9 (Acceptable Use) above."
            )}
          </p>
        </>
      ),
    },
    {
      id: "disclaimers",
      navLabel: t("terms.sections.disclaimers.navLabel", "Disclaimers"),
      title: t("terms.sections.disclaimers.title", "13. Disclaimers of Warranties"),
      body: (
        <>
          <p>
            {t(
              "terms.sections.disclaimers.para1",
              "THE SERVICE, INCLUDING ALL COURSE CONTENT AND CERTIFICATES, IS PROVIDED “AS IS” AND “AS AVAILABLE,” WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS, IMPLIED, OR STATUTORY, INCLUDING IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, TIMELY, SECURE, OR ERROR-FREE, THAT DEFECTS WILL BE CORRECTED, OR THAT THE SERVICE WILL MEET YOUR PARTICULAR REQUIREMENTS OR CAREER OUTCOMES."
            )}
          </p>
          <p>
            {t("terms.sections.disclaimers.para2", "Some jurisdictions do not allow the exclusion of certain warranties, so some of the above exclusions may not apply to you, in which case our warranties will be limited to the minimum extent permitted by applicable law.")}
          </p>
        </>
      ),
    },
    {
      id: "liability",
      navLabel: t("terms.sections.liability.navLabel", "Limitation of Liability"),
      title: t("terms.sections.liability.title", "14. Limitation of Liability"),
      body: (
        <>
          <p>
            {t(
              "terms.sections.liability.para1",
              "TO THE FULLEST EXTENT PERMITTED BY LAW, IN NO EVENT WILL {{appNameUpper}}, SMARTTECH SOLUTIONS, OR OUR OFFICERS, EMPLOYEES, AGENTS, OR LICENSORS BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, REVENUE, DATA, GOODWILL, OR OTHER INTANGIBLE LOSSES, ARISING OUT OF OR RELATING TO YOUR ACCESS TO OR USE OF, OR INABILITY TO ACCESS OR USE, THE SERVICE, EVEN IF WE HAVE BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.",
              { appNameUpper }
            )}
          </p>
          <p>
            {t(
              "terms.sections.liability.para2",
              "TO THE FULLEST EXTENT PERMITTED BY LAW, OUR TOTAL AGGREGATE LIABILITY FOR ANY CLAIM ARISING OUT OF OR RELATING TO THESE TERMS OR THE SERVICE WILL NOT EXCEED THE GREATER OF (A) THE TOTAL AMOUNT YOU PAID US, IF ANY, FOR EVENT TICKETS IN THE 12 MONTHS BEFORE THE CLAIM AROSE, OR (B) ONE HUNDRED U.S. DOLLARS (US$100)."
            )}
          </p>
          <p>
            {t("terms.sections.liability.para3", "Nothing in these Terms limits any liability that cannot be limited or excluded under applicable law, including liability for death or personal injury caused by negligence, or for fraud.")}
          </p>
        </>
      ),
    },
    {
      id: "indemnification",
      navLabel: t("terms.sections.indemnification.navLabel", "Indemnification"),
      title: t("terms.sections.indemnification.title", "15. Indemnification"),
      body: (
        <p>
          {t(
            "terms.sections.indemnification.para1",
            "You agree to defend, indemnify, and hold harmless {{appName}}, SmartTech Solutions, and our respective officers, employees, and agents from and against any claims, liabilities, damages, losses, and expenses, including reasonable legal fees, arising out of or in any way connected with: (a) your access to or use of the Service; (b) your User Content; (c) your violation of these Terms; or (d) your violation of any applicable law or the rights of a third party.",
            { appName }
          )}
        </p>
      ),
    },
    {
      id: "governing-law",
      navLabel: t("terms.sections.governingLaw.navLabel", "Governing Law & Disputes"),
      title: t("terms.sections.governingLaw.title", "16. Governing Law and Dispute Resolution"),
      body: (
        <>
          <p>
            {t("terms.sections.governingLaw.para1", "These Terms are governed by the laws applicable in the jurisdiction in which SmartTech Solutions is registered, without regard to conflict-of-law principles.")}
          </p>
          <h3>{t("terms.sections.governingLaw.subheading1", "Informal resolution first")}</h3>
          <p>
            {t("terms.sections.governingLaw.para2", "Before filing a claim, you agree to first contact us at the email address in Section 19 and give us 30 days to try to resolve the dispute informally. Most concerns can be resolved this way.")}
          </p>
          <h3>{t("terms.sections.governingLaw.subheading2", "Binding arbitration")}</h3>
          <p>
            {t(
              "terms.sections.governingLaw.para3",
              "If a dispute is not resolved informally, you and {{appName}} agree that it will be resolved by binding, individual arbitration rather than in court, except that either party may bring an individual claim in small-claims court if it qualifies, and either party may seek injunctive relief in court for infringement or misuse of intellectual property. Arbitration will be conducted by a neutral, reputable arbitration provider under its standard rules for consumer disputes, will take place (or be conducted by written submissions or video conference) in a manner reasonably convenient to you, and each party will generally bear its own costs, except as the arbitrator or applicable rules provide otherwise for consumer claims.",
              { appName }
            )}
          </p>
          <div className="rbt-legal-callout">
            <i className="feather-info" aria-hidden="true"></i>
            <p>
              <strong>{t("terms.sections.governingLaw.calloutLabel", "Opt-out:")}</strong>
              {t("terms.sections.governingLaw.calloutText", " you may opt out of this arbitration agreement by emailing us at the address in Section 19 within 30 days of first accepting these Terms, stating clearly that you decline to arbitrate. If you opt out, disputes will be resolved in the courts with jurisdiction over the parties.")}
            </p>
          </div>
          <p>
            {t("terms.sections.governingLaw.para4", "To the extent permitted by law, any arbitration or proceeding will be conducted only on an individual basis and not as a class, collective, or representative action.")}
          </p>
        </>
      ),
    },
    {
      id: "changes",
      navLabel: t("terms.sections.changes.navLabel", "Changes to These Terms"),
      title: t("terms.sections.changes.title", "17. Changes to These Terms"),
      body: (
        <p>
          {t(
            "terms.sections.changes.para1",
            "We may update these Terms from time to time to reflect changes to the Service, applicable law, or our practices. If we make material changes, we will notify you by email, by an in-app notification, or by posting a notice on the Service at least 14 days before the changes take effect, except where a shorter period is required for legal or security reasons. Your continued use of the Service after the effective date of a change constitutes your acceptance of the updated Terms."
          )}
        </p>
      ),
    },
    {
      id: "severability",
      navLabel: t("terms.sections.severability.navLabel", "Severability"),
      title: t("terms.sections.severability.title", "18. Severability"),
      body: (
        <p>
          {t(
            "terms.sections.severability.para1",
            "If any provision of these Terms is found to be unenforceable or invalid under applicable law, that provision will be limited or eliminated to the minimum extent necessary so that these Terms will otherwise remain in full force and effect and enforceable between you and {{appName}}.",
            { appName }
          )}
        </p>
      ),
    },
    {
      id: "contact",
      navLabel: t("terms.sections.contact.navLabel", "Contact Us"),
      title: t("terms.sections.contact.title", "19. Contact Us"),
      body: (
        <p>
          {t("terms.sections.contact.para1Before", "Questions about these Terms should be sent to ")}
          <Link href={`mailto:${CONFIG.EMAIL_1}`}>{CONFIG.EMAIL_1}</Link>
          {t("terms.sections.contact.para1Middle", ", or via our ")}
          <Link href={ROUTE.contact ?? "/contact"}>{t("terms.sections.contact.para1LinkText", "Contact page")}</Link>
          {t("terms.sections.contact.para1After", ". We aim to respond to all legal and account inquiries promptly.")}
        </p>
      ),
    },
  ];
};

const TermsOfService = () => {
  const { t } = useTranslation("legal");
  const sections = useSections(t);

  return (
    <main className="rbt-main-wrapper">
      <LegalPageLayout
        eyebrow={t("terms.eyebrow", "Legal")}
        title={t("terms.title", "Terms of Use")}
        description={t(
          "terms.description",
          "The rules for using SmartTech Academy — written in plain language, kept fair, and organized so you can jump straight to what matters to you."
        )}
        lastUpdated={LAST_UPDATED}
        sections={sections}
      />

      <CallToActionOne />

      <div
        className="rbt-newsletter-area bg-color-primary newsletter-style-2 rbt-section-gap"
        style={{ background: "url(" + image_url("../app_images/bg/dots.png") + ") repeat center/auto" }}
      >
        <Newsletter />
      </div>
    </main>
  );
};

export default TermsOfService;
